import axios from "axios";
import Post, { PostType } from "@/models/post";
import { getHeroImageFromPost } from '@/utils/postImageExtraction';
import type { CreatePostPayload, PostLocationPayload, RawLocationData } from "@/utils/itinerary/types";
import type { RawPhoto } from "@/utils/postTypes";
import type GooglePlacePhoto from "@/models/googlePlacePhoto";
import { error, debug } from '@/utils/logger';
import { getReferralCreatorIdForApi } from '@/lib/analyticsAttribution';
import { isMongoObjectIdString, mongoObjectIdStringOrNull } from '@/utils/idGeneration';
import { createOrUpdateListPost } from './list';
import type { ListPostPayload, ListPostLocationPayload } from '@/utils/listBuilder';
import PostLocation from '@/models/postLocation';
import Location from '@/models/location';
import { runWithConcurrencyLimit } from '@/utils/concurrencyUtils';
import { retryOnRateLimit } from '@/utils/retryOnRateLimit';
import { convertPlaceToPostLocation, isStayPlaceCard } from '@/utils/placeToPostLocationConverter';
import { UNSCHEDULED_DAY_INDEX } from '@/utils/itinerary/unscheduled';
import { extractFingerprintKeysFromPost } from '@/utils/placeFingerprint';
import { extractCityFromPost } from '@/utils/cityExtractionFromPost';
import { getCityDisplayText } from '@/utils/cityUtils';
import type { PrimaryDestination } from '@/types/city';
import type { PlaceCardData } from '@/types/listViewTypes';
import type { SourceMetadata } from '@/utils/postBuilder';
import {
  mergeOverviewNotesForAppend,
  resolveSourceMetadataForAppend,
  type ImportAppendToExistingOptions,
} from '@/utils/itineraryImportAppend';

interface ApiError extends Error {
  status?: number;
  response?: unknown;
}

// const API_URL = 'http://localhost:8081';
const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL || 'https://app-be.globethrivers.com';

export const GET_POST = `${API_URL}/api/v2/posts/post_types`;
export const GET_ITINERARY = `${API_URL}/api/v2/ml/itinerary-prediction-engine`;
export const CREATE_POST = `${API_URL}/api/v2/posts`;
// CREATE_LIST_POST moved to src/api/list.tsx
export const MOBILE_POST = `${API_URL}/api/v2/mobile/post`;
export const ITINERARY_POST = `${API_URL}/api/v2/posts/post_types`;
export const GET_SAVED_POST = `${API_URL}/api/v2/saves/posts`;
export const SEARCH_STRING = `${API_URL}/api/v2/posts/user`;
export const GET_POST_BY_ID = `${API_URL}/api/v2/post`;
export const GET_EXTERNAL_SOURCE_POSTS = `${API_URL}/api/v2/posts/external-sources`;

export type ItineraryBrowseFetchOptions = {
  limit?: number;
  page?: number;
  signal?: AbortSignal;
};

function isRequestAborted(err: unknown): boolean {
  if (axios.isCancel(err)) {
    return true;
  }
  const e = err as { name?: string; code?: string };
  return e.name === 'AbortError' || e.code === 'ERR_CANCELED';
}

function extractPostArrayFromResponse(data: unknown): unknown[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === 'object' && Array.isArray((data as { posts?: unknown[] }).posts)) {
    return (data as { posts: unknown[] }).posts;
  }
  return [];
}

/** Map cached or fresh API rows to itinerary Post instances. */
export function itineraryPostsFromRaw(raw: unknown[]): Post[] {
  const posts = raw.map((item) => new Post(item as Record<string, unknown>));
  return posts.filter((post) => post.post_type === PostType.Itinerary);
}

export async function fetchExternalSourcePosts(limit: number = 3): Promise<{ posts: Post[], metadata: Record<string, unknown> }> {
  try {
    const response = await axios.get(GET_EXTERNAL_SOURCE_POSTS, {
      params: { limit, photoFormat: "identifiers" }
    });
    return {
      posts: response.data.posts?.map((item: Record<string, unknown>) => new Post(item)) ?? [],
      metadata: response.data.metadata
    };
  } catch (err) {
    error("Error fetching external source posts:", err);
    return { posts: [], metadata: { total: 0, limit, generatedAt: new Date().toISOString() } };
  }
}

/**
 * Fetches raw itinerary browse rows from /api/v2/posts/user (before Post mapping).
 * Use with itineraryPostsFromRaw for caching.
 */
export async function fetchItineraryPostsRaw(
  searchString: string,
  selectedTripTypes: string[] = [],
  options: ItineraryBrowseFetchOptions = {}
): Promise<unknown[]> {
  const params: Record<string, unknown> = {
    searchString,
    photoFormat: 'identifiers',
    onlyItineraries: 'true',
    limit: options.limit,
    page: options.page,
  };

  if (selectedTripTypes.includes('draft')) {
    params.onlyDrafts = true;
  }
  if (selectedTripTypes.includes('private')) {
    params.onlyPrivate = true;
  }
  if (selectedTripTypes.includes('published')) {
    params.onlyPublished = true;
  }

  const response = await axios.get(SEARCH_STRING, {
    params,
    signal: options.signal,
  });
  return extractPostArrayFromResponse(response.data);
}

export async function fetchItineraryPosts(
  searchString: string,
  selectedTripTypes: string[] = [],
  options: ItineraryBrowseFetchOptions = {}
): Promise<Post[]> {
  try {
    const raw = await fetchItineraryPostsRaw(searchString, selectedTripTypes, options);
    const filteredPosts = itineraryPostsFromRaw(raw);

    try {
      debug('[fetchItineraryPosts] API response', {
        endpoint: SEARCH_STRING,
        limit: options.limit,
        page: options.page,
        postsCount: raw.length,
        filteredPostsCount: filteredPosts.length,
      });
    } catch (logError) {
      error('[fetchItineraryPosts] Error in logging:', logError);
    }

    return filteredPosts;
  } catch (err) {
    if (isRequestAborted(err)) {
      throw err;
    }
    error('Error fetching itinerary posts:', err);
    return [];
  }
}

/**
 * Fetch posts/itineraries that contain a specific location
 * Uses the optimized /api/v2/posts/location endpoint
 * 
 * @param locationId - The location ID to search for
 * @returns Array of Post objects that contain this location
 */
export async function fetchPostsByLocationId(locationId: string): Promise<Post[]> {
  // Validate locationId
  if (!locationId || typeof locationId !== 'string' || locationId.trim().length === 0) {
    error('[fetchPostsByLocationId] Invalid locationId provided', { locationId });
    return [];
  }

  try {
    // Use /api/v2/posts/location endpoint which properly filters by location and post_type
    const endpoint = `${API_URL}/api/v2/posts/location/${locationId}`;
    
    if (!endpoint || !endpoint.startsWith('http')) {
      throw new Error(`Invalid API URL: ${API_URL}`);
    }
    
    const params: Record<string, unknown> = {
      post_type: 'Itinerary', // Backend accepts post_type (singular) or post_types (plural)
      photoFormat: "identifiers",
      page: 1,
      limit: 50, // Reasonable limit for modal display
    };

    // Get auth headers if available (for private posts visibility)
    const headers: Record<string, string> = {};
    try {
      const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
      if (rawAccessToken) {
        const accessToken = rawAccessToken.startsWith('Bearer ')
          ? rawAccessToken.substring(7)
          : rawAccessToken;
        headers['Authorization'] = accessToken;
        
        const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
        if (rawIdToken) {
          const idToken = rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken;
          headers['x-id-token'] = idToken;
        }
      }
    } catch (authErr) {
      // If auth fails, continue without auth (public posts only)
      error('[fetchPostsByLocationId] Error getting auth headers:', authErr);
    }

    const response = await axios.get(endpoint, { params, headers });
    
    debug('[fetchPostsByLocationId] API response', {
      endpoint,
      locationId,
      status: response.status,
      hasData: !!response.data,
      dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
      hasPagination: !!response.data?.pagination,
      rawPostsCount: response.data?.pagination ? response.data.posts?.length : (Array.isArray(response.data) ? response.data.length : 0),
    });
    
    // Handle paginated response
    const data = response.data;
    const posts = data.pagination 
      ? (data.posts || []).map((item: Record<string, unknown>) => new Post(item))
      : (Array.isArray(data) 
          ? data.map((item: Record<string, unknown>) => new Post(item))
          : []);
    
    debug('[fetchPostsByLocationId] Posts after mapping', {
      locationId,
      totalPosts: posts.length,
      postTypes: posts.map((p: Post) => ({ id: p._id, name: p.name, type: p.post_type })),
    });
    
    // Backend filters by post_type, but validate location is in post as safety check
    const filteredPosts = posts.filter((post: Post) => {
      if (!Array.isArray(post.postLocations)) {
        debug('[fetchPostsByLocationId] Post has no postLocations array', {
          postId: post._id,
        });
        return false;
      }
      
      const hasLocation = post.postLocations.some((pl: PostLocation) => {
        const plLocationId = pl.location?._id;
        return plLocationId === locationId;
      });
      
      if (!hasLocation) {
        debug('[fetchPostsByLocationId] Post does not contain target location', {
          postId: post._id,
          targetLocationId: locationId,
          postLocationIds: post.postLocations.map((pl: PostLocation) => pl.location?._id),
        });
      }
      
      return hasLocation;
    });

    debug('[fetchPostsByLocationId] Final filtered posts', {
      locationId,
      filteredCount: filteredPosts.length,
      filteredPostIds: filteredPosts.map((p: Post) => p._id),
    });

    return filteredPosts;
  } catch (err) {
    error('[fetchPostsByLocationId] Error fetching posts by location ID:', err);
    return [];
  }
}

/**
 * Fetch lists that contain a specific location
 * Uses the optimized /api/v2/posts/location endpoint
 *
 * @param locationId - The location ID to search for
 * @returns Array of Post objects (List type) that contain this location
 */
export async function fetchListsByLocationId(locationId: string): Promise<Post[]> {
  // Validate locationId
  if (!locationId || typeof locationId !== 'string' || locationId.trim().length === 0) {
    error('[fetchListsByLocationId] Invalid locationId provided', { locationId });
    return [];
  }

  try {
    // Use /api/v2/posts/location endpoint which properly filters by location and post_type
    const endpoint = `${API_URL}/api/v2/posts/location/${locationId}`;
    
    if (!endpoint || !endpoint.startsWith('http')) {
      throw new Error(`Invalid API URL: ${API_URL}`);
    }
    
    const params: Record<string, unknown> = {
      post_type: 'List', // Backend accepts post_type (singular) or post_types (plural)
      photoFormat: "identifiers",
      page: 1,
      limit: 50, // Reasonable limit for modal display
    };

    // Get auth headers if available (for private posts visibility)
    const headers: Record<string, string> = {};
    try {
      const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
      if (rawAccessToken) {
        const accessToken = rawAccessToken.startsWith('Bearer ')
          ? rawAccessToken.substring(7)
          : rawAccessToken;
        headers['Authorization'] = accessToken;
        
        const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
        if (rawIdToken) {
          const idToken = rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken;
          headers['x-id-token'] = idToken;
        }
      }
    } catch (authErr) {
      // If auth fails, continue without auth (public posts only)
      error('[fetchListsByLocationId] Error getting auth headers:', authErr);
    }

    const response = await axios.get(endpoint, { params, headers });
    
    // Handle paginated response
    const data = response.data;
    const posts = data.pagination 
      ? (data.posts || []).map((item: Record<string, unknown>) => new Post(item))
      : (Array.isArray(data) 
          ? data.map((item: Record<string, unknown>) => new Post(item))
          : []);
    
    // Backend filters by post_type, but validate location is in post as safety check
    const filteredPosts = posts.filter((post: Post) => {
      if (!Array.isArray(post.postLocations)) return false;
      
      return post.postLocations.some((pl: PostLocation) => {
        const plLocationId = pl.location?._id;
        return plLocationId === locationId;
      });
    });

    return filteredPosts;
  } catch (err) {
    error('[fetchListsByLocationId] Error fetching lists by location ID:', err);
    return [];
  }
}

/**
 * Fetch Place-type posts that contain a specific location.
 * Uses the same /api/v2/posts/location endpoint as fetchListsByLocationId,
 * requesting post_type=Place. Returns [] on error or when no Place posts match.
 *
 * @param locationId - The location ID to search for
 * @returns Array of Post objects (Place type) that contain this location
 */
export async function fetchPlacesByLocationId(locationId: string): Promise<Post[]> {
  if (!locationId || typeof locationId !== 'string' || locationId.trim().length === 0) {
    error('[fetchPlacesByLocationId] Invalid locationId provided', { locationId });
    return [];
  }

  try {
    const endpoint = `${API_URL}/api/v2/posts/location/${locationId}`;

    const params: Record<string, unknown> = {
      post_type: 'Place',
      photoFormat: 'identifiers',
      page: 1,
      limit: 50,
    };

    const headers: Record<string, string> = {};
    try {
      const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
      if (rawAccessToken) {
        const accessToken = rawAccessToken.startsWith('Bearer ')
          ? rawAccessToken.substring(7)
          : rawAccessToken;
        headers['Authorization'] = accessToken;

        const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
        if (rawIdToken) {
          const idToken = rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken;
          headers['x-id-token'] = idToken;
        }
      }
    } catch (authErr) {
      error('[fetchPlacesByLocationId] Error getting auth headers:', authErr);
    }

    const response = await axios.get(endpoint, { params, headers });
    const data = response.data;
    const posts = data.pagination
      ? (data.posts || []).map((item: Record<string, unknown>) => new Post(item))
      : Array.isArray(data)
        ? data.map((item: Record<string, unknown>) => new Post(item))
        : [];

    const filteredPosts = posts.filter((post: Post) => {
      if (!Array.isArray(post.postLocations)) return false;
      return post.postLocations.some((pl: PostLocation) => pl.location?._id === locationId);
    });

    return filteredPosts;
  } catch (err) {
    error('[fetchPlacesByLocationId] Error fetching places by location ID:', err);
    return [];
  }
}

/**
 * Pagination shape returned by /api/v2/posts/location/:locationId
 */
export interface LocationPostsPagination {
  page: number;
  pageSize: number;
  totalPosts: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Fetch both itineraries and lists that contain a specific location in a single call.
 * Uses post_types=Itinerary,List so the backend returns both in one paginated response.
 *
 * @param locationId - The location ID to search for
 * @param options - Optional limit (default/max 24) and page (default 1)
 * @returns Object with posts array and pagination metadata
 */
export async function fetchPostsByLocationIdUnified(
  locationId: string,
  options: { limit?: number; page?: number } = {}
): Promise<{ posts: Post[]; pagination: LocationPostsPagination }> {
  const emptyResult: { posts: Post[]; pagination: LocationPostsPagination } = {
    posts: [],
    pagination: { page: 1, pageSize: 0, totalPosts: 0, totalPages: 0, hasNext: false, hasPrevious: false },
  };

  if (!locationId || typeof locationId !== 'string' || locationId.trim().length === 0) {
    error('[fetchPostsByLocationIdUnified] Invalid locationId provided', { locationId });
    return emptyResult;
  }

  // Validate MongoDB ObjectID format before hitting the API
  const isValidMongoId = /^[a-f\d]{24}$/i.test(locationId);
  debug('[fetchPostsByLocationIdUnified] Called', {
    locationId,
    isValidMongoId,
  });

  if (!isValidMongoId) {
    debug('[fetchPostsByLocationIdUnified] locationId failed MongoDB ObjectID validation — skipping API call', { locationId });
    return emptyResult;
  }

  try {
    const endpoint = `${API_URL}/api/v2/posts/location/${locationId}`;

    if (!endpoint || !endpoint.startsWith('http')) {
      throw new Error(`Invalid API URL: ${API_URL}`);
    }

    const limit = Math.min(options.limit ?? 24, 24);
    const page = options.page ?? 1;

    const params: Record<string, unknown> = {
      post_types: 'Itinerary,List',
      photoFormat: 'identifiers',
      page,
      limit,
    };

    const headers: Record<string, string> = {};
    try {
      const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
      if (rawAccessToken) {
        const accessToken = rawAccessToken.startsWith('Bearer ')
          ? rawAccessToken.substring(7)
          : rawAccessToken;
        headers['Authorization'] = accessToken;

        const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
        if (rawIdToken) {
          const idToken = rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken;
          headers['x-id-token'] = idToken;
        }
      }
    } catch (authErr) {
      error('[fetchPostsByLocationIdUnified] Error getting auth headers:', authErr);
    }

    debug('[fetchPostsByLocationIdUnified] Sending request', { endpoint, params });
    const response = await axios.get(endpoint, { params, headers });
    const data = response.data;

    debug('[fetchPostsByLocationIdUnified] Raw API response', {
      hasPagination: !!data.pagination,
      apiTotalPosts: data.pagination?.totalPosts,
      rawPostCount: Array.isArray(data.posts) ? data.posts.length : (Array.isArray(data) ? data.length : 0),
    });

    const posts: Post[] = data.pagination
      ? (data.posts || []).map((item: Record<string, unknown>) => new Post(item))
      : Array.isArray(data)
        ? data.map((item: Record<string, unknown>) => new Post(item))
        : [];

    const pagination: LocationPostsPagination = data.pagination
      ? {
          page: data.pagination.page ?? page,
          pageSize: data.pagination.pageSize ?? limit,
          totalPosts: data.pagination.totalPosts ?? posts.length,
          totalPages: data.pagination.totalPages ?? 1,
          hasNext: data.pagination.hasNext ?? false,
          hasPrevious: data.pagination.hasPrevious ?? false,
        }
      : {
          page: 1,
          pageSize: posts.length,
          totalPosts: posts.length,
          totalPages: 1,
          hasNext: false,
          hasPrevious: false,
        };

    // The API already returns only posts containing this location, so we trust it as the
    // authoritative source. The client-side filter below is a safety net only, checking
    // both pl.location?._id and pl.location_id because API responses often omit the full
    // nested location object (only location_id string is present), which previously caused
    // all posts to be filtered out (showing "Featured in 0 Trips & Lists" incorrectly).
    // If postLocations is missing entirely, include the post rather than dropping it.
    const filteredPosts = posts.filter((post: Post) => {
      if (!Array.isArray(post.postLocations)) return true; // trust API - no postLocations to check
      // Accept either the embedded location object _id or the location_id string field
      return post.postLocations.some(
        (pl: PostLocation) =>
          pl.location?._id === locationId ||
          pl.location_id === locationId
      );
    });

    debug('[fetchPostsByLocationIdUnified] Filter result', {
      locationId,
      rawPostCount: posts.length,
      filteredPostCount: filteredPosts.length,
      // For each post: show its id, and for each postLocation show whether it matched
      postFilterDetails: posts.map((post: Post) => ({
        postId: post._id,
        hasPostLocations: Array.isArray(post.postLocations),
        postLocationCount: Array.isArray(post.postLocations) ? post.postLocations.length : 0,
        matchedByLocationObjectId: Array.isArray(post.postLocations)
          ? post.postLocations.some((pl: PostLocation) => pl.location?._id === locationId)
          : false,
        matchedByLocationIdField: Array.isArray(post.postLocations)
          ? post.postLocations.some((pl: PostLocation) => pl.location_id === locationId)
          : false,
        sampleLocationIds: Array.isArray(post.postLocations)
          ? post.postLocations.slice(0, 3).map((pl: PostLocation) => ({
              location_id: pl.location_id,
              location_obj_id: pl.location?._id,
            }))
          : [],
      })),
    });

    // Use API pagination for total count (authoritative); update pageSize to match what we returned
    const correctedPagination: LocationPostsPagination = {
      ...pagination,
      pageSize: filteredPosts.length,
    };

    debug('[fetchPostsByLocationIdUnified] Result', {
      locationId,
      rawCount: posts.length,
      filteredCount: filteredPosts.length,
      pagination: correctedPagination,
    });

    return { posts: filteredPosts, pagination: correctedPagination };
  } catch (err) {
    error('[fetchPostsByLocationIdUnified] Error:', err);
    // Callers show a sign-in prompt on auth errors instead of an empty list
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    if (status === 401 || status === 403) throw err;
    return {
      posts: [],
      pagination: { page: 1, pageSize: 0, totalPosts: 0, totalPages: 0, hasNext: false, hasPrevious: false },
    };
  }
}

/**
 * Per-location payload from POST /api/v1/locations/counts with include_all_types: true.
 * by_type.Place is always present (Pascal-case, defaults to 0). place_count is a convenience
 * duplicate when include_all_types is true.
 */
interface LocationsCountsApiEntry {
  by_type?: {
    Itinerary?: number;
    List?: number;
    Place?: number;
    [key: string]: number | undefined;
  };
  itinerary_count?: number;
  list_count?: number;
  place_count?: number;
  total_count?: number;
}

export function parseLocationCountsFromApiEntry(raw: unknown): {
  itineraryCount: number;
  listCount: number;
  placeCount: number;
} {
  if (!raw || typeof raw !== 'object') {
    return { itineraryCount: 0, listCount: 0, placeCount: 0 };
  }
  const e = raw as LocationsCountsApiEntry;
  const itineraryCount = e.by_type?.Itinerary ?? e.itinerary_count ?? 0;
  const listCount = e.by_type?.List ?? e.list_count ?? 0;
  const placeCount = e.by_type?.Place ?? e.place_count ?? 0;
  return { itineraryCount, listCount, placeCount };
}

/** Backend limit for POST /api/v1/locations/counts location_ids per request */
const LOCATION_COUNTS_BATCH_SIZE = 100;

type LocationCountTriple = { itineraryCount: number; listCount: number; placeCount: number };

function buildLocationCountsRequestHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  try {
    const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
    if (rawAccessToken) {
      const accessToken = rawAccessToken.startsWith('Bearer ')
        ? rawAccessToken.substring(7)
        : rawAccessToken;
      headers['Authorization'] = accessToken;

      const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
      if (rawIdToken) {
        const idToken = rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken;
        headers['x-id-token'] = idToken;
      }
    }
  } catch (authErr) {
    error('[fetchLocationCountsBatch] Error getting auth headers:', authErr);
  }
  return headers;
}

/**
 * Single POST /api/v1/locations/counts for at most LOCATION_COUNTS_BATCH_SIZE ids.
 */
async function fetchLocationCountsSingleRequest(
  chunkIds: string[],
  headers: Record<string, string>,
): Promise<Record<string, LocationCountTriple>> {
  const endpoint = `${API_URL}/api/v1/locations/counts`;

  if (!endpoint || !endpoint.startsWith('http')) {
    throw new Error(`Invalid API URL: ${API_URL}`);
  }

  const response = await axios.post(
    endpoint,
    {
      location_ids: chunkIds,
      include_all_types: true,
    },
    { headers },
  );

  if (!response || !response.data) {
    throw new Error('Invalid API response: missing data');
  }

  const { counts, meta } = response.data;

  debug('[fetchLocationCountsBatch] API response chunk', {
    endpoint,
    chunkSize: chunkIds.length,
    hasCounts: !!counts,
    hasMeta: !!meta,
    meta,
    sampleCounts: chunkIds.slice(0, 3).reduce((acc, id) => {
      acc[id] = counts?.[id];
      return acc;
    }, {} as Record<string, unknown>),
  });

  if (!counts || typeof counts !== 'object') {
    error('[fetchLocationCountsBatch] Invalid response: counts is not an object', {
      responseData: response.data,
    });
    const empty: Record<string, LocationCountTriple> = {};
    chunkIds.forEach((locationId) => {
      empty[locationId] = { itineraryCount: 0, listCount: 0, placeCount: 0 };
    });
    return empty;
  }

  const result: Record<string, LocationCountTriple> = {};
  chunkIds.forEach((locationId) => {
    const locationCounts = counts[locationId];
    if (locationCounts && typeof locationCounts === 'object') {
      const parsed = parseLocationCountsFromApiEntry(locationCounts);
      result[locationId] = parsed;

      if (parsed.itineraryCount > 0 || parsed.listCount > 0 || parsed.placeCount > 0) {
        debug('[fetchLocationCountsBatch] Location counts', {
          locationId,
          ...parsed,
          byType: (locationCounts as LocationsCountsApiEntry).by_type,
          rawCounts: locationCounts,
        });
      }
    } else {
      result[locationId] = { itineraryCount: 0, listCount: 0, placeCount: 0 };
    }
  });

  return result;
}

/**
 * Fetch counts for multiple locations via POST /api/v1/locations/counts.
 * Automatically chunks into requests of up to {@link LOCATION_COUNTS_BATCH_SIZE} IDs and merges results.
 *
 * @param locationIds - Array of location IDs (any length; deduped, invalid entries dropped)
 * @returns Map of location ID to itineraryCount, listCount, and placeCount
 */
export async function fetchLocationCountsBatch(
  locationIds: string[]
): Promise<Record<string, LocationCountTriple>> {
  if (!locationIds) {
    error('[fetchLocationCountsBatch] locationIds is null or undefined');
    return {};
  }

  if (!Array.isArray(locationIds)) {
    error('[fetchLocationCountsBatch] locationIds is not an array', {
      type: typeof locationIds,
      value: locationIds,
    });
    return {};
  }

  if (locationIds.length === 0) {
    return {};
  }

  const validLocationIds = locationIds.filter(
    (id) => id && typeof id === 'string' && id.trim().length > 0,
  );

  if (validLocationIds.length === 0) {
    error('[fetchLocationCountsBatch] No valid location IDs provided', {
      originalCount: locationIds.length,
      invalidIds: locationIds.filter((id) => !id || typeof id !== 'string' || id.trim().length === 0),
    });
    return {};
  }

  const uniqueOrdered = [...new Set(validLocationIds)];

  if (uniqueOrdered.length > LOCATION_COUNTS_BATCH_SIZE) {
    debug('[fetchLocationCountsBatch] chunking requests', {
      totalIds: uniqueOrdered.length,
      batchSize: LOCATION_COUNTS_BATCH_SIZE,
      chunkCount: Math.ceil(uniqueOrdered.length / LOCATION_COUNTS_BATCH_SIZE),
    });
  }

  const chunks: string[][] = [];
  for (let i = 0; i < uniqueOrdered.length; i += LOCATION_COUNTS_BATCH_SIZE) {
    chunks.push(uniqueOrdered.slice(i, i + LOCATION_COUNTS_BATCH_SIZE));
  }

  const headers = buildLocationCountsRequestHeaders();
  const merged: Record<string, LocationCountTriple> = {};

  const settled = await Promise.allSettled(
    chunks.map((chunk) => fetchLocationCountsSingleRequest(chunk, headers)),
  );

  settled.forEach((outcome, index) => {
    const chunk = chunks[index];
    if (outcome.status === 'fulfilled') {
      Object.assign(merged, outcome.value);
    } else {
      error('[fetchLocationCountsBatch] Chunk request failed', {
        chunkIndex: index,
        chunkSize: chunk.length,
        reason: outcome.reason,
      });
      chunk.forEach((locationId) => {
        merged[locationId] = { itineraryCount: 0, listCount: 0, placeCount: 0 };
      });
    }
  });

  return merged;
}

export async function fetchItineraryDrafts(selectedTripTypes: string[] = []): Promise<Post[]> {
  try {
    const params: Record<string, unknown> = { photoFormat: "identifiers" };

    if (selectedTripTypes.includes("draft")) {
      params.onlyDrafts = true;
    }

    const response = await axios.get(SEARCH_STRING, { params });
    return Array.isArray(response.data)
      ? response.data.map((item: Record<string, unknown>) => new Post(item))
      : [];
  } catch (err) {
    error("Error fetching itinerary posts:", err);
    return [];
  }
}

/** Raw saved itinerary rows from /api/v2/saves/posts (before Post mapping). */
export async function fetchSavedPostsRaw(
  options: ItineraryBrowseFetchOptions = {}
): Promise<unknown[]> {
  const response = await axios.get(GET_SAVED_POST, {
    params: {
      photoFormat: 'identifiers',
      onlyItineraries: 'true',
      limit: options.limit,
      page: options.page,
    },
    signal: options.signal,
  });
  return extractPostArrayFromResponse(response.data);
}

export async function fetchSavedPosts(
  options: ItineraryBrowseFetchOptions = {}
): Promise<Post[]> {
  try {
    const raw = await fetchSavedPostsRaw(options);
    const filteredPosts = itineraryPostsFromRaw(raw);

    try {
      debug('[fetchSavedPosts] API response', {
        endpoint: GET_SAVED_POST,
        limit: options.limit,
        page: options.page,
        postsCount: raw.length,
        filteredPostsCount: filteredPosts.length,
      });
    } catch (logError) {
      error('[fetchSavedPosts] Error in logging:', logError);
    }

    return filteredPosts;
  } catch (err) {
    if (isRequestAborted(err)) {
      throw err;
    }
    error('Error fetching saved itinerary posts:', err);
    return [];
  }
}

export async function getItinerarypost(): Promise<Post[]> {
  try {
    const response = await axios.get(
      `${GET_POST}?postTypes=Itinerary&limit=10&photoFormat=identifiers`
    );
    return Array.isArray(response.data)
      ? response.data.map((item: Record<string, unknown>) => new Post(item))
      : [];
  } catch (err) {
    error('[getItinerarypost] Error fetching post types:', err);
    throw err;
  }
}

export async function getItineraryList(data: Record<string, unknown>) {
  const response = await axios.post(`${GET_ITINERARY}`, data);
  return response.data;
}

export async function createPost(data: CreatePostPayload): Promise<Post> {
  try {
    const plList = Array.isArray(data?.postLocations) ? data.postLocations : [];
    debug('[createPost] Request payload', {
      post_name: String(data?.name ?? '').slice(0, 80),
      postLocations_count: plList.length,
    });

    // NOTE: We deliberately send the lean payload directly to the backend (no framework proxy layer)
    // because build / remix / edit flows were failing once we started buffering huge
    // postLocations blobs through Next.js. If the backend ever requires richer data again,
    // we will need to coordinate on increasing the accepted payload size server-side.

    const referralCreatorId = getReferralCreatorIdForApi();
    const requestPayload =
      referralCreatorId != null
        ? { ...data, referral_creator_id: referralCreatorId }
        : data;

    const response = await axios.post(CREATE_POST, requestPayload);
    return new Post(response.data);
  } catch (err: unknown) {
    const axiosError = err && typeof err === 'object' && 'response' in err
      ? err as { response?: { status?: number; statusText?: string; data?: unknown } }
      : null;
    const errorResponse = axiosError?.response;
    
    error('[createPost API] Request failed', {
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      responseData: errorResponse?.data,
      requestUrl: CREATE_POST,
      postName: data?.name,
      postType: data?.post_type,
      postLocationsCount: Array.isArray(data?.postLocations) ? data.postLocations.length : 0,
    });
    
    if (errorResponse?.data) {
      error('[createPost API] Backend error response:', JSON.stringify(errorResponse.data, null, 2));
    }
    
    throw err;
  }
}

function toRawPhotos(photos: GooglePlacePhoto[]): RawPhoto[] {
  return photos.map((p) => ({
    name: p.name,
    photo_reference: p.photo_reference,
    author:
      p.author === undefined
        ? undefined
        : p.author === null
          ? null
          : {
              displayName: p.author.displayName,
              uri: p.author.uri ?? null,
            },
  }));
}

/**
 * Build location payload from Location object
 * Extracted to reduce code duplication between List and Itinerary branches
 */
function buildLocationPayload(location: Location): RawLocationData {
  return {
    _id: location._id || '',
    name: location.name || '',
    city_identifying_name: location.city_identifying_name,
    google_place_id: location.google_place_id,
    geoposition: location.geoposition ? {
      coordinates: location.geoposition.coordinates as [number, number],
    } : undefined,
    a3_intl_address: location.a3_intl_address,
    photos: toRawPhotos(location.photos),
    google_places_data: location.google_places_data,
  };
}

/**
 * Convert PostLocation to List format payload
 * Lists use 'order' field instead of itinerary_day_index/itinerary_day_order_index
 */
function convertPostLocationToListFormat(
  postLocation: PostLocation,
  index: number,
  postId: string
): ListPostLocationPayload {
  const listPostLocation: ListPostLocationPayload = {
    _id: postLocation._id,
    post_id: postLocation.post_id || postId,
    location: postLocation.location ? buildLocationPayload(postLocation.location) : {},
    body: postLocation.body || '',
    image_urls: postLocation.image_urls || [],
    order: index, // Lists use 'order' field instead of itinerary_day_index
    notes: postLocation.notes || '',
    timeRange: postLocation.timeRange || null,
    is_user_created: postLocation.is_user_created || false,
  };

  // Add optional fields if they exist
  if (postLocation.gt_category) {
    listPostLocation.gt_category = postLocation.gt_category;
    listPostLocation.slotType = postLocation.slotType;
  }
  if (postLocation.budget !== undefined && postLocation.budget !== null) {
    listPostLocation.budget = postLocation.budget;
  }
  // Add address for user-created places (PostLocation model already has address field)
  if (postLocation.is_user_created && postLocation.address) {
    listPostLocation.address = postLocation.address;
  }

  return listPostLocation;
}

/**
 * Convert PostLocation to Itinerary format payload
 * Itineraries use itinerary_day_index and itinerary_day_order_index
 */
function convertPostLocationToItineraryFormat(
  postLocation: PostLocation,
  postId: string
): PostLocationPayload {
  const loc = postLocation.location;
  const hasNoLinkedLocation = !loc?._id && !loc?.google_place_id;
  const isUserCreatedResolved = !!(
    postLocation.is_user_created ||
    hasNoLinkedLocation
  );

  const payload: PostLocationPayload = {
    _id: postLocation._id,
    post_id: postLocation.post_id || postId,
    location_id: loc?._id || '',
    body: postLocation.body || '',
    itinerary_day_index: postLocation.itinerary_day_index,
    itinerary_day_order_index: postLocation.itinerary_day_order_index,
    image_urls: postLocation.image_urls || [],
    gt_category: postLocation.gt_category,
    slotType: postLocation.slotType,
    notes: postLocation.notes,
    budget: postLocation.budget,
    timeRange: postLocation.timeRange,
    is_user_created: isUserCreatedResolved,
  };

  // User-created stops: send name, address, and explicit title so photo/cover updates
  // don't strip fields (backend replaces all post_locations).
  if (isUserCreatedResolved) {
    const explicitName = postLocation.name?.trim();
    if (postLocation.address) {
      payload.address = postLocation.address;
    }
    if (explicitName) {
      payload.name = explicitName;
    } else if (postLocation.body) {
      payload.name = postLocation.body;
    }
  }

  // Include location data (minimal required fields)
  if (loc) {
    payload.location = buildLocationPayload(loc);
  }

  return payload;
}

/**
 * Build base post fields common to both Lists and Itineraries
 */
function applyImportPrimaryDestinationForAppend(
  post: Post,
  appendOptions?: ImportAppendToExistingOptions,
): Partial<{ primary_destination: PrimaryDestination; city_identifying_names: string[] }> {
  if (!appendOptions?.primaryDestination || post.primary_destination) {
    return {};
  }
  const patch: Partial<{ primary_destination: PrimaryDestination; city_identifying_names: string[] }> =
    {
      primary_destination: appendOptions.primaryDestination,
    };
  const existingCities = post.city_identifying_names ?? [];
  if (existingCities.length === 0 && appendOptions.cityIdentifyingNames?.length) {
    patch.city_identifying_names = appendOptions.cityIdentifyingNames;
  }
  return patch;
}

function buildBasePostFields(post: Post, updatedImageUrls: string[]) {
  const sourceSocialMediaPostId = mongoObjectIdStringOrNull(
    (post as { source_social_media_post_id?: string }).source_social_media_post_id,
  );
  return {
    _id: post._id,
    name: post.name || '',
    body: post.body || '',
    city_identifying_names: post.city_identifying_names || [],
    image_urls: updatedImageUrls,
    is_draft: post.is_draft || false,
    is_private: post.is_private || false,
    tag_list: post.tag_list || [],
    travel_with_types: post.travel_with_types || [],
    collaborators_email: post.collaborators_email || [],
    budget: post.budget || null,
    // These callers re-send the whole post (cover photo, categorization, place photos): echo
    // the Overview notes back, or the rewrite clears them.
    ...(post.post_type === PostType.List
      ? post.list_notes != null ? { list_notes: post.list_notes } : {}
      : post.itinerary_notes != null ? { itinerary_notes: post.itinerary_notes } : {}),
    ...(post.source_metadata ? { source_metadata: post.source_metadata } : {}),
    ...(sourceSocialMediaPostId
      ? { source_social_media_post_id: sourceSocialMediaPostId }
      : {}),
  };
}

function deriveFallbackCoverImageUrlFromPost(post: Post): string | null {
  if (!Array.isArray(post.postLocations) || post.postLocations.length === 0) {
    return null;
  }

  for (const postLocation of post.postLocations) {
    const location = postLocation.location;
    const locationHero = location?.largePhotoURI || location?.thumbnailPhotoURI || location?.main_photo;
    if (typeof locationHero === 'string' && locationHero.trim().length > 0) {
      return locationHero.trim();
    }

    const firstPostLocationImage = Array.isArray(postLocation.image_urls)
      ? postLocation.image_urls.find((url) => typeof url === 'string' && url.trim().length > 0)
      : null;
    if (typeof firstPostLocationImage === 'string' && firstPostLocationImage.trim().length > 0) {
      return firstPostLocationImage.trim();
    }
  }

  return null;
}

/**
 * Update post cover photo
 * Fetches existing post, updates image_urls[0] with new cover photo URL, and saves
 * 
 * NOTE: This function builds a full post payload because the backend requires
 * all fields when updating. The cover photo is stored as image_urls[0].
 * 
 * @param postId - Post ID to update
 * @param imageUrl - New cover photo URL (will be stored as image_urls[0]). If empty string, removes cover photo.
 * @returns Updated Post object
 */
export async function updatePostCoverPhoto(postId: string, imageUrl: string): Promise<Post> {
  try {
    // Fetch existing post to get all fields
    const existingPost = await getPostById(postId);

    // Update image_urls array (use first item as cover photo)
    const updatedImageUrls = [...(existingPost.image_urls || [])];
    
    if (!imageUrl) {
      // Remove cover photo: remove first element if it exists
      if (updatedImageUrls.length > 0) {
        updatedImageUrls.shift(); // Remove first element (cover photo)
      }
      if (updatedImageUrls.length === 0) {
        // Prefer a deterministic place-derived cover before falling back to defaults.
        const placeDerivedCover = deriveFallbackCoverImageUrlFromPost(existingPost);
        if (placeDerivedCover) {
          updatedImageUrls.push(placeDerivedCover);
        } else if (existingPost.cities?.length > 0) {
          // Legacy fallback for posts that still carry city-level photos.
          const cityPhotoUrl = existingPost.cities[0].largePhotoURI;
          if (cityPhotoUrl) {
            updatedImageUrls.push(cityPhotoUrl);
          }
        }
      }
    } else if (updatedImageUrls.length === 0) {
      // If no existing images, add the new one
      updatedImageUrls.push(imageUrl);
    } else {
      // Replace first image (cover photo)
      updatedImageUrls[0] = imageUrl;
    }

    // Handle Lists and Itineraries differently
    if (existingPost.post_type === PostType.List) {
      // Lists use a flat payload structure and different endpoint
      const baseFields = buildBasePostFields(existingPost, updatedImageUrls);
      const listPayload: ListPostPayload = {
        ...baseFields,
        _id: postId,
        post_type: 'List',
        category_ids: existingPost.category_ids || [],
        // Include ALL existing PostLocations (backend replaces all, so we must include all)
        // Convert PostLocation objects to list format (uses 'order' instead of itinerary fields)
        postLocations: existingPost.postLocations.map((postLocation, index) =>
          convertPostLocationToListFormat(postLocation, index, postId)
        ),
      };

      // Use createOrUpdateListPost for Lists (sends to /api/v1/post endpoint)
      return await createOrUpdateListPost(listPayload);
    } else {
      // Itineraries use wrapped payload structure
      const baseFields = buildBasePostFields(existingPost, updatedImageUrls);
      const updatePayload: CreatePostPayload = {
        _id: postId,
        post: {
          ...baseFields,
          _id: postId,
          post_type: existingPost.post_type || 'Itinerary',
          is_hidden: existingPost.is_hidden || false,
          vibe_description: existingPost.vibe_description || '',
          itinerary_data: existingPost.itinerary_data,
          // Itineraries use collaborators_emails (plural) instead of collaborators_email
          collaborators_emails: existingPost.collaborators_email || [],
        },
        // Include ALL existing PostLocations (backend replaces all, so we must include all)
        // Convert PostLocation objects to PostLocationPayload format
        // This matches the pattern used in the typed post route clients
        postLocations: existingPost.postLocations.map((postLocation) =>
          convertPostLocationToItineraryFormat(postLocation, postId)
        ),
        itinerary_slots: [], // Not updating itinerary structure
      };

      // Use updatePost for Itineraries (sends to CREATE_POST endpoint with _id)
      return await updatePost(postId, updatePayload);
    }
  } catch (err) {
    error('[updatePostCoverPhoto] Failed to update cover photo:', err);
    throw err;
  }
}

/**
 * Update PostLocation photos
 * Fetches existing post, updates image_urls for specific PostLocation, and saves
 * 
 * NOTE: This function builds a full post payload because the backend requires
 * all fields when updating, and ALL PostLocations must be included.
 * 
 * @param postId - Post ID containing the PostLocation
 * @param postLocationId - PostLocation ID to update
 * @param imageUrls - New image URLs array (will replace existing image_urls)
 * @returns Updated Post object
 */
export async function updatePostLocationPhotos(
  postId: string,
  postLocationId: string,
  imageUrls: string[]
): Promise<Post> {
  try {
    debug('[updatePostLocationPhotos] Updating place photos', {
      postId,
      postLocationId,
      image_urls_count: imageUrls.length,
    });

    // Fetch existing post to get all fields
    const existingPost = await getPostById(postId);

    // Find the PostLocation to update
    const postLocationToUpdate = existingPost.postLocations.find(
      pl => pl._id === postLocationId
    );

    if (!postLocationToUpdate) {
      const errorMsg = `PostLocation with ID ${postLocationId} not found in post ${postId}`;
      error('[updatePostLocationPhotos] PostLocation not found', {
        postId,
        postLocationId,
        availablePostLocationIds: existingPost.postLocations.map(pl => pl._id),
      });
      throw new Error(errorMsg);
    }

    // Handle Lists and Itineraries differently
    if (existingPost.post_type === PostType.List) {
      // Preserve cover photo (image_urls on the post itself)
      const existingCoverPhotoUrls = existingPost.image_urls || [];
      
      // Lists use a flat payload structure and different endpoint
      const baseFields = buildBasePostFields(existingPost, existingCoverPhotoUrls);
      const listPayload: ListPostPayload = {
        ...baseFields,
        _id: postId,
        post_type: 'List',
        category_ids: existingPost.category_ids || [],
        // Include ALL existing PostLocations (backend replaces all, so we must include all)
        postLocations: existingPost.postLocations.map((postLocation, index) => {
          const listFormat = convertPostLocationToListFormat(postLocation, index, postId);
          // Update image_urls only for the target PostLocation
          if (postLocation._id === postLocationId) {
            listFormat.image_urls = imageUrls;
          }
          return listFormat;
        }),
      };

      // Use createOrUpdateListPost for Lists (sends to /api/v1/post endpoint)
      return await createOrUpdateListPost(listPayload);
    } else {
      // Preserve cover photo (image_urls on the post itself)
      const existingCoverPhotoUrls = existingPost.image_urls || [];
      
      // Itineraries use wrapped payload structure
      const baseFields = buildBasePostFields(existingPost, existingCoverPhotoUrls);
      const updatePayload: CreatePostPayload = {
        _id: postId,
        post: {
          ...baseFields,
          _id: postId,
          post_type: existingPost.post_type || 'Itinerary',
          image_urls: existingCoverPhotoUrls, // Explicitly preserve cover photo
          is_hidden: existingPost.is_hidden || false,
          vibe_description: existingPost.vibe_description || '',
          itinerary_data: existingPost.itinerary_data,
          // Itineraries use collaborators_emails (plural) instead of collaborators_email
          collaborators_emails: existingPost.collaborators_email || [],
        },
        // Include ALL existing PostLocations (backend replaces all, so we must include all)
        postLocations: existingPost.postLocations.map(pl => {
          const payload = convertPostLocationToItineraryFormat(pl, postId);
          // Update image_urls only for the target PostLocation
          if (pl._id === postLocationId) {
            payload.image_urls = imageUrls;
          }
          return payload;
        }),
        itinerary_slots: [], // Not updating itinerary structure
      };

      // Use updatePost for Itineraries (sends to CREATE_POST endpoint with _id)
      return await updatePost(postId, updatePayload);
    }
  } catch (err) {
    error('[updatePostLocationPhotos] Failed to update PostLocation photos', {
      postId,
      postLocationId,
      error: err,
      errorMessage: err instanceof Error ? err.message : String(err),
    });
    throw err;
  }
}

export async function updatePost(postId: string, data: CreatePostPayload): Promise<Post> {
  try {
    const plList = Array.isArray(data?.postLocations) ? data.postLocations : [];
    debug('[updatePost] Request payload', {
      postId,
      post_name: String(data?.name ?? '').slice(0, 80),
      postLocations_count: plList.length,
    });

    // NOTE: Update requests now hit the same endpoint as creates. This matches the behaviour we
    // had before introducing the proxy and avoids the CORS / payload limit issues. If edits
    // start requiring heavier payloads, the backend limit must be increased instead of
    // routing through Next.js.

    const response = await retryOnRateLimit(
      () => axios.post(CREATE_POST, data),
      { label: 'updatePost' },
    );
    return new Post(response.data);
  } catch (err: unknown) {
    const axiosError = err && typeof err === 'object' && 'response' in err
      ? err as { response?: { status?: number; data?: { message?: string } }; message?: string }
      : null;
    const status = axiosError?.response?.status;
    const message = axiosError?.response?.data?.message || axiosError?.message || (err instanceof Error ? err.message : 'Unknown error');
    
    error('[updatePost API] Request failed', {
      postId,
      status: status ?? '(no response — network/CORS error)',
      message: message || '(empty)',
      responseData: axiosError?.response?.data ?? null,
    });
    if (err instanceof Error) {
      error('[updatePost API] Error details:', err.name, err.message, err.stack?.split('\n')[1]);
    }
    
    // Handle specific error cases based on backend API documentation
    if (status === 403) {
      const errorMessage = 'Unauthorized: You don\'t have permission to edit this itinerary';
      const customError: ApiError = new Error(errorMessage);
      customError.status = 403;
      customError.response = axiosError?.response;
      throw customError;
    } else if (status === 404) {
      const errorMessage = 'Itinerary not found';
      const customError: ApiError = new Error(errorMessage);
      customError.status = 404;
      customError.response = axiosError?.response;
      throw customError;
    } else if (status === 400) {
      const errorMessage = `Invalid request: ${message}`;
      const customError: ApiError = new Error(errorMessage);
      customError.status = 400;
      customError.response = axiosError?.response;
      throw customError;
    }
    
    // Re-throw original error for other cases
    throw err;
  }
}

// createOrUpdateListPost moved to src/api/list.tsx

/**
 * Update post privacy setting immediately via PATCH
 * Uses dedicated endpoint for atomic privacy updates
 * 
 * @param postId - Post ID to update
 * @param isPrivate - New privacy setting
 * @returns Updated Post object
 */
export async function updatePostPrivacy(postId: string, isPrivate: boolean): Promise<Post> {
  try {
    const response = await axios.patch(
      `${API_URL}/api/v1/post/${postId}/privacy`,
      { is_private: isPrivate }
    );
    return new Post(response.data);
  } catch (err: unknown) {
    const axiosError = err && typeof err === 'object' && 'response' in err
      ? err as { response?: { status?: number; data?: { message?: string } }; message?: string }
      : null;
    const status = axiosError?.response?.status;
    const message = axiosError?.response?.data?.message || axiosError?.message || (err instanceof Error ? err.message : 'Unknown error');
    
    error('[updatePostPrivacy API] Request failed', {
      postId,
      isPrivate,
      status,
      message,
      responseData: axiosError?.response?.data,
    });
    
    if (status === 403) {
      const errorMessage = 'Unauthorized: You don\'t have permission to change this post\'s privacy';
      const customError: ApiError = new Error(errorMessage);
      customError.status = 403;
      customError.response = axiosError?.response;
      throw customError;
    } else if (status === 404) {
      const errorMessage = 'Post not found';
      const customError: ApiError = new Error(errorMessage);
      customError.status = 404;
      customError.response = axiosError?.response;
      throw customError;
    }
    
    throw err;
  }
}

/**
 * Update post categorization (trip types, travel with, budget)
 * Fetches existing post, updates metadata fields, and saves
 * 
 * @param postId - Post ID to update
 * @param metadata - New categorization data
 * @returns Updated Post object
 */
export async function updatePostCategorization(
  postId: string,
  metadata: { tag_list: string[]; travel_with_types: string[]; budget: string | null }
): Promise<Post> {
  try {
    // Fetch existing post to get all fields
    const existingPost = await getPostById(postId);

    // Handle Lists and Itineraries differently
    if (existingPost.post_type === PostType.List) {
      // Lists use a flat payload structure
      const baseFields = buildBasePostFields(existingPost, existingPost.image_urls || []);
      const listPayload: ListPostPayload = {
        ...baseFields,
        _id: postId,
        post_type: 'List',
        category_ids: existingPost.category_ids || [],
        tag_list: metadata.tag_list,
        travel_with_types: metadata.travel_with_types,
        // The creator edited them, so the backend stores them and stops auto-filling.
        categories_source: 'user',
        budget: metadata.budget,
        // Include ALL existing PostLocations (backend replaces all)
        postLocations: existingPost.postLocations.map((postLocation, index) =>
          convertPostLocationToListFormat(postLocation, index, postId)
        ),
      };

      debug('[updatePostCategorization] Updating list categorization', {
        postId,
        metadata,
      });

      return await createOrUpdateListPost(listPayload);
    } else {
      // Itineraries: same payload shape as updatePostCoverPhoto so backend gets full place data.
      // Backend replaces all post_locations with what we send; we must send postLocations (not only itinerary_slots).
      const baseFields = buildBasePostFields(existingPost, existingPost.image_urls || []);
      const updatePayload: CreatePostPayload = {
        _id: postId,
        post: {
          ...baseFields,
          _id: postId,
          tag_list: metadata.tag_list,
          travel_with_types: metadata.travel_with_types,
          // The creator edited them, so the backend stores them and stops auto-filling.
          categories_source: 'user',
          budget: metadata.budget,
          post_type: PostType.Itinerary,
          is_hidden: existingPost.is_hidden || false,
          vibe_description: existingPost.vibe_description || '',
          itinerary_data: existingPost.itinerary_data,
          collaborators_emails: existingPost.collaborators_email || [],
          category_ids: existingPost.category_ids || [],
        },
        postLocations: existingPost.postLocations.map((postLocation) =>
          convertPostLocationToItineraryFormat(postLocation, postId)
        ),
        itinerary_slots: [],
      };

      debug('[updatePostCategorization] Updating itinerary categorization', {
        postId,
        metadata,
        postLocationsCount: updatePayload.postLocations?.length ?? 0,
      });

      return await updatePost(postId, updatePayload);
    }
  } catch (err: unknown) {
    error('[updatePostCategorization] Failed to update post categorization', {
      postId,
      metadata,
      error: err,
      errorMessage: err instanceof Error ? err.message : String(err),
    });
    throw err;
  }
}

/**
 * Lower-level fetch returning both the raw API response and a constructed `Post`.
 * Use this from route loaders that need to cache the raw record alongside the model;
 * `getPostById` (returning only `Post`) delegates here.
 */
export async function getPostByIdWithRaw(
  postId: string,
  options?: { signal?: AbortSignal }
): Promise<{ post: Post; raw: Record<string, unknown> }> {
  try {
    const response = await axios.get(`${GET_POST_BY_ID}/${postId}`, {
      params: { photoFormat: "identifiers" },
      signal: options?.signal,
    });

    const raw = (response.data ?? {}) as Record<string, unknown>;
    const post = new Post(raw);
    
    // Log API response for recommendation_count and place rec_count (safely, minimal logging)
    try {
      const firstPostLocation = Array.isArray(post.postLocations) && post.postLocations.length > 0 
        ? (post.postLocations[0] as unknown as Record<string, unknown>)
        : null;
      const firstLocation = firstPostLocation?.location;
      const firstLocationKeys = firstLocation ? Object.keys(firstLocation).slice(0, 20) : [];
      
      // Also check raw response data structure
      const rawFirstPostLocation = Array.isArray(response.data.post_locations) && response.data.post_locations.length > 0
        ? response.data.post_locations[0]
        : null;
      const rawFirstLocation = rawFirstPostLocation?.location;
      const rawFirstLocationKeys = rawFirstLocation ? Object.keys(rawFirstLocation).slice(0, 20) : [];
      
      debug('[getPostById] API response', {
        endpoint: `${GET_POST_BY_ID}/${postId}`,
        postId: post._id,
        postName: post.name,
        postType: post.post_type,
        recommendation_count: post.recommendation_count,
        hasRecommendations: Array.isArray(post.recommendations) && post.recommendations.length > 0,
        postLocationsCount: Array.isArray(post.postLocations) ? post.postLocations.length : 0,
        // Sample first location rec_count if available (from Post model)
        firstLocationRecCount: (firstLocation as Record<string, unknown>)?.rec_count,
        firstLocationRecCountCamel: (firstLocation as Record<string, unknown>)?.recCount,
        firstLocationHasRecCount: typeof (firstLocation as Record<string, unknown>)?.rec_count === 'number' || typeof (firstLocation as Record<string, unknown>)?.recCount === 'number',
        firstLocationKeys: firstLocationKeys,
        firstPostLocationHasRecCount: typeof (firstPostLocation as Record<string, unknown>)?.rec_count === 'number' || typeof (firstPostLocation as Record<string, unknown>)?.recCount === 'number',
        firstPostLocationRecCount: (firstPostLocation as Record<string, unknown>)?.rec_count ?? (firstPostLocation as Record<string, unknown>)?.recCount,
        // Raw API response structure (before Post model transformation)
        rawFirstLocationRecCount: (rawFirstLocation as Record<string, unknown>)?.rec_count,
        rawFirstLocationRecCountCamel: (rawFirstLocation as Record<string, unknown>)?.recCount,
        rawFirstLocationHasRecCount: typeof (rawFirstLocation as Record<string, unknown>)?.rec_count === 'number' || typeof (rawFirstLocation as Record<string, unknown>)?.recCount === 'number',
        rawFirstLocationKeys: rawFirstLocationKeys,
      });
    } catch (_logError) {
      // Don't let logging errors break the API call - silently fail
    }

    return { post, raw };
  } catch (err) {
    if (!isRequestAborted(err)) {
      error('[getPostById] Error fetching post:', err);
    }
    throw err;
  }
}

export async function getPostById(
  postId: string,
  options?: { signal?: AbortSignal }
): Promise<Post> {
  const { post } = await getPostByIdWithRaw(postId, options);
  return post;
}

/**
 * Interface for saved itinerary summary
 */
export interface SavedItinerary {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
  imageUrl?: string;
  imageUrlFallback?: string;
  /**
   * Deduped place identity keys harvested from postLocations at fetch time.
   * Formats: "g:<googlePlaceId>", "m:<mongoLocationId>", "u:<normalizedTitle>|<normalizedAddress>"
   * Used by SaveToOverlay for O(1) per-row duplicate detection.
   */
  placeFingerprintKeys?: string[];
  /** City/country label for card surfaces (extracted from post at fetch time). */
  cityDisplayText?: string;
  /** Place count for card surfaces (from post locations at fetch time). */
  locationsCount?: number;
}

function resolveLocationsCountFromPost(
  raw: Post | Record<string, unknown>,
  postObj: Post,
): number {
  const fromArray = postObj.postLocations?.length ?? postObj.post_locations?.length ?? 0;
  if (fromArray > 0) return fromArray;

  if (raw instanceof Post) return 0;

  const data = raw as Record<string, unknown>;
  for (const key of ['locations_count', 'post_locations_count', 'location_count']) {
    const value = data[key];
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      return value;
    }
  }

  return 0;
}

function mapPostToSavedItinerary(
  post: Post | Record<string, unknown>,
  defaultName: string,
): SavedItinerary {
  const postObj = post instanceof Post ? post : new Post(post);
  const heroImage = getHeroImageFromPost(postObj);
  const cityData = extractCityFromPost(postObj);
  const cityDisplayText = cityData ? getCityDisplayText(cityData) : undefined;

  return {
    id: postObj._id,
    name: postObj.name || defaultName,
    created_at:
      postObj.created_at instanceof Date
        ? postObj.created_at.toISOString()
        : (postObj.created_at as string | undefined),
    updated_at:
      postObj.updated_at instanceof Date
        ? postObj.updated_at.toISOString()
        : (postObj.updated_at as string | undefined),
    imageUrl: heroImage.primary || undefined,
    imageUrlFallback: heroImage.fallback || undefined,
    placeFingerprintKeys: extractFingerprintKeysFromPost(postObj),
    cityDisplayText,
    locationsCount: resolveLocationsCountFromPost(post, postObj),
  };
}

/**
 * Pagination info for user itineraries
 */
export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalPosts: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Options for fetching user itineraries
 */
export interface GetUserItinerariesOptions {
  userId?: string;
  page?: number;
  limit?: number;
  includeDrafts?: boolean;
  includePrivate?: boolean;
}

function parseUserSearchPosts(responseData: unknown): Post[] {
  const postsArray = Array.isArray(responseData)
    ? responseData
    : (responseData as { posts?: unknown[] })?.posts || [];
  return postsArray.map((post) =>
    post instanceof Post ? post : new Post(post as Record<string, unknown>),
  );
}

async function fetchUserSearchPosts(
  params: Record<string, string | number>,
): Promise<{ posts: Post[]; pagination?: PaginationInfo }> {
  const response = await axios.get(SEARCH_STRING, { params });
  const pagination = (response.data as { pagination?: PaginationInfo }).pagination;

  if (pagination && (response.data as { posts?: unknown[] }).posts) {
    return { posts: parseUserSearchPosts(response.data), pagination };
  }

  return { posts: parseUserSearchPosts(response.data) };
}

function buildUserSearchParams(
  options: GetUserItinerariesOptions,
  extra: Record<string, string | number>,
): Record<string, string | number> {
  const { userId, page, limit = 20, includeDrafts = false, includePrivate = false } = options;
  const params: Record<string, string | number> = { ...extra };

  if (userId) params.user_id = userId;
  if (page !== undefined) {
    params.page = page;
    params.limit = limit;
  }
  if (includeDrafts) params.allowDrafts = 'true';
  if (includePrivate) params.allowPrivate = 'true';

  return params;
}

/**
 * Get the current user's itinerary posts as full Post objects (for card surfaces).
 */
export async function getUserItineraryPosts(
  options: GetUserItinerariesOptions = {},
): Promise<{ posts: Post[]; pagination?: PaginationInfo }> {
  try {
    return await fetchUserSearchPosts(
      buildUserSearchParams(options, { onlyItineraries: 'true' }),
    );
  } catch (err) {
    error('[getUserItineraryPosts] Error fetching user itineraries:', err);
    throw err;
  }
}

/**
 * Get the current user's list posts as full Post objects (for card surfaces).
 */
export async function getUserListPosts(
  options: GetUserItinerariesOptions = {},
): Promise<{ posts: Post[]; pagination?: PaginationInfo }> {
  try {
    const { posts, pagination } = await fetchUserSearchPosts(
      buildUserSearchParams(options, { post_type: 'List' }),
    );
    const listPosts = posts.filter((post) => post.post_type === PostType.List);
    return { posts: listPosts, pagination };
  } catch (err) {
    error('[getUserListPosts] Error fetching user lists:', err);
    throw err;
  }
}

/**
 * Get user's saved itineraries
 * 
 * @param options - Query options for fetching itineraries
 * @returns Object with posts array and optional pagination info
 */
export async function getUserItineraries(
  options: GetUserItinerariesOptions = {}
): Promise<{ posts: SavedItinerary[]; pagination?: PaginationInfo }> {
  try {
    const { posts, pagination } = await getUserItineraryPosts(options);
    return {
      posts: posts.map((post) => mapPostToSavedItinerary(post, 'Untitled Itinerary')),
      pagination,
    };
  } catch (err) {
    error('[getUserItineraries] Error fetching user itineraries:', err);
    throw err;
  }
}

/**
 * Add multiple places to an existing itinerary in one read + one write.
 * New places are appended to the bottom (end of the last day) in the given order, so existing
 * rows keep their position and the derived cover photo (first place image) does not change.
 *
 * IMPORTANT: the backend replaces the whole post and ALL postLocations on update, so every
 * existing field is echoed back along with the existing + new rows. Anything left out is
 * reset — omitting is_private / is_draft turned a private or draft trip into a posted one.
 */
export async function addPlacesToItineraryAtomic(
  itineraryId: string,
  places: PlaceCardData[],
  appendOptions?: ImportAppendToExistingOptions,
): Promise<Post> {
  if (!places.length) {
    throw new Error('[addPlacesToItineraryAtomic] places array is empty');
  }
  try {
    const existingItinerary = await getPostById(itineraryId);

    if (existingItinerary.post_type !== PostType.Itinerary) {
      throw new Error(`Post ${itineraryId} is not an Itinerary`);
    }

    const existingPostLocations: PostLocationPayload[] = (existingItinerary.postLocations || []).map(
      (pl: PostLocation) => {
        const payload = convertPostLocationToItineraryFormat(pl, itineraryId);
        if (pl.address) {
          payload.address = pl.address;
        }
        return payload;
      },
    );

    const cityAddress = existingItinerary.city_identifying_names?.[0] || '';

    // Append to the end of the last day so new places land at the bottom of the trip.
    // Hotels go to the bottom of Unscheduled instead, until the traveler puts them on a day.
    const lastDayIndex = existingPostLocations.reduce(
      (max, pl) => Math.max(max, pl.itinerary_day_index ?? 0),
      0,
    );
    const lastOrderIn = (dayIndex: number) =>
      existingPostLocations.reduce(
        (max, pl) =>
          (pl.itinerary_day_index ?? 0) === dayIndex
            ? Math.max(max, pl.itinerary_day_order_index ?? 0)
            : max,
        -1,
      );
    const nextOrderByDay = new Map<number, number>([
      [lastDayIndex, lastOrderIn(lastDayIndex) + 1],
      [UNSCHEDULED_DAY_INDEX, lastOrderIn(UNSCHEDULED_DAY_INDEX) + 1],
    ]);
    const placements = places.map((place) => {
      const dayIndex = isStayPlaceCard(place) ? UNSCHEDULED_DAY_INDEX : lastDayIndex;
      const orderIndex = nextOrderByDay.get(dayIndex)!;
      nextOrderByDay.set(dayIndex, orderIndex + 1);
      return { place, dayIndex, orderIndex };
    });

    const ATOMIC_PLACE_ENRICH_CONCURRENCY = 4;
    const newPostLocations = await runWithConcurrencyLimit(
      placements.map(
        ({ place, dayIndex, orderIndex }) => () =>
          convertPlaceToPostLocation(place, dayIndex, orderIndex, cityAddress),
      ),
      ATOMIC_PLACE_ENRICH_CONCURRENCY,
    );

    const updatedPostLocations: PostLocationPayload[] = [...existingPostLocations, ...newPostLocations];

    const existingCoverImageUrls = Array.isArray(existingItinerary.image_urls)
      ? existingItinerary.image_urls
      : [];

    // A trip without a post-level cover keeps deriving one from its places.
    const { image_urls: _coverImageUrls, ...baseFields } = buildBasePostFields(
      existingItinerary,
      existingCoverImageUrls,
    );

    const sourceMetadata = resolveSourceMetadataForAppend(
      existingItinerary.source_metadata,
      appendOptions?.importSourceMetadata,
    );
    const existingSocialId = mongoObjectIdStringOrNull(
      (existingItinerary as { source_social_media_post_id?: string }).source_social_media_post_id,
    );
    const appendSocialId = mongoObjectIdStringOrNull(appendOptions?.sourceSocialMediaPostId);
    const mergedItineraryNotes = mergeOverviewNotesForAppend(
      existingItinerary.itinerary_notes,
      appendOptions?.appendOverviewNotes,
    );
    const destinationPatch = applyImportPrimaryDestinationForAppend(
      existingItinerary,
      appendOptions,
    );

    const updatePayload: CreatePostPayload = {
      post: {
        ...baseFields,
        _id: itineraryId,
        post_type: 'Itinerary',
        is_hidden: existingItinerary.is_hidden || false,
        vibe_description: existingItinerary.vibe_description || '',
        itinerary_data: existingItinerary.itinerary_data,
        // Itineraries use collaborators_emails (plural) instead of collaborators_email
        collaborators_emails: existingItinerary.collaborators_email || [],
        itinerary_notes: mergedItineraryNotes || null,
        ...(sourceMetadata !== undefined ? { source_metadata: sourceMetadata } : {}),
        ...(!existingSocialId && appendSocialId
          ? { source_social_media_post_id: appendSocialId }
          : {}),
        ...destinationPatch,
        // Echo the existing cover photo back so adding places never replaces it.
        ...(existingCoverImageUrls.length > 0 ? { image_urls: existingCoverImageUrls } : {}),
      },
      postLocations: updatedPostLocations,
    };

    return await updatePost(itineraryId, updatePayload);
  } catch (err) {
    error('[addPlacesToItineraryAtomic] Error adding places to itinerary:', err);
    throw err;
  }
}

/**
 * Add a place to an existing itinerary.
 *
 * @param itineraryId - ID of the itinerary to add place to
 * @param place - Place card data to add
 * @returns Updated Post object
 */
export async function addPlaceToItinerary(
  itineraryId: string,
  place: PlaceCardData,
): Promise<Post> {
  return addPlacesToItineraryAtomic(itineraryId, [place]);
}

/**
 * Create a new itinerary with a place as the first item
 * 
 * @param title - Title for the new itinerary
 * @param place - Place card data to add as first item
 * @param isDraft - Whether to create as draft (default: false)
 * @param options - Optional destination metadata for new post creation
 * @returns Created Post object
 */
interface CreateWithPlaceOptions {
  primaryDestination?: PrimaryDestination | null;
  cityIdentifyingNames?: string[];
  sourceMetadata?: SourceMetadata;
  /** Post-level list description (maps to `list_notes` on create). */
  listNotes?: string;
  sourceSocialMediaPostId?: string | null;
}

function deriveCoverImageUrlsFromPlace(place: PlaceCardData): string[] {
  if (!Array.isArray(place.imageUrls) || place.imageUrls.length === 0) {
    return [];
  }
  const firstValid = place.imageUrls.find((url) => typeof url === 'string' && url.trim().length > 0);
  return firstValid ? [firstValid] : [];
}

export async function createItineraryWithPlace(
  title: string,
  place: PlaceCardData,
  isDraft: boolean = false,
  options: CreateWithPlaceOptions = {}
): Promise<Post> {
  try {
    // Convert place to PostLocationPayload
    const postLocation = await convertPlaceToPostLocation(
      place,
      isStayPlaceCard(place) ? UNSCHEDULED_DAY_INDEX : 0,
      0,
    );
    const coverImageUrls = deriveCoverImageUrlsFromPlace(place);

    const cityIdentifyingNames = options.cityIdentifyingNames ?? [];

    // Create new itinerary
    const createPayload: CreatePostPayload = {
      post: {
        name: title,
        post_type: 'Itinerary', // Capital I
        is_draft: isDraft,
        is_private: true,
        city_identifying_names: cityIdentifyingNames, // Optional
        ...(coverImageUrls.length > 0 ? { image_urls: coverImageUrls } : {}),
        ...(options.primaryDestination ? { primary_destination: options.primaryDestination } : {}),
      },
      postLocations: [postLocation], // Single place as first item
    };

    return await createPost(createPayload);
  } catch (err) {
    error('[createItineraryWithPlace] Error creating itinerary with place:', err);
    throw err;
  }
}

// ─── List helpers (mirror of itinerary helpers above) ────────────────────────

function listPostLocationPayloadFromEnrichedPlace(
  payload: PostLocationPayload,
  order: number,
): ListPostLocationPayload {
  const loc = payload.location;
  const locRecord: Record<string, unknown> =
    loc && typeof loc === 'object' && !Array.isArray(loc) ? { ...(loc as Record<string, unknown>) } : {};

  const googleFromLoc =
    typeof locRecord.google_place_id === 'string' ? locRecord.google_place_id.trim() : '';
  const payloadRecord = payload as Record<string, unknown>;
  const googleFromPayload =
    typeof payloadRecord.google_place_id === 'string' ? String(payloadRecord.google_place_id).trim() : '';
  const googlePlaceId = googleFromLoc || googleFromPayload || null;

  const rawLocationId = payload.location_id;
  const fromPayloadId =
    typeof rawLocationId === 'string' && isMongoObjectIdString(rawLocationId) ? rawLocationId : null;
  const fromLocId =
    typeof locRecord._id === 'string' && isMongoObjectIdString(String(locRecord._id))
      ? String(locRecord._id)
      : null;
  const locationMongoId = fromPayloadId || fromLocId;

  const isUserCreated = Boolean(payload.is_user_created);

  const categoryFromLoc =
    typeof locRecord.gt_category === 'string' ? locRecord.gt_category : undefined;

  const listSlot: ListPostLocationPayload = {
    ...(locationMongoId ? { location_id: locationMongoId } : {}),
    ...(googlePlaceId ? { google_place_id: googlePlaceId } : {}),
    location: locRecord,
    body: payload.body || '',
    image_urls: Array.isArray(payload.image_urls) ? payload.image_urls : [],
    order,
    notes: typeof payload.notes === 'string' ? payload.notes : '',
    timeRange: payload.timeRange ?? null,
    is_user_created: isUserCreated,
    gt_category: payload.gt_category ?? categoryFromLoc,
    slotType: payload.slotType,
  };

  if (payload.budget !== undefined && payload.budget !== null) {
    listSlot.budget = payload.budget;
  }
  if (isUserCreated && payload.address) {
    listSlot.address = payload.address;
  }

  return listSlot;
}

/**
 * Add multiple places to an existing list in one read + one write.
 * New slots are appended after the existing slots in the given order, so existing rows keep
 * their position and the derived cover photo (first slot image) does not change.
 *
 * IMPORTANT: the backend replaces the whole post and ALL postLocations on update, so every
 * existing field is echoed back along with the existing + new rows. Anything left out is
 * reset — omitting is_private / is_draft turned a private or draft list into a posted one.
 */
export async function addPlacesToListAtomic(
  listId: string,
  places: PlaceCardData[],
  appendOptions?: ImportAppendToExistingOptions,
): Promise<Post> {
  if (!places.length) {
    throw new Error('[addPlacesToListAtomic] places array is empty');
  }
  try {
    const existingList = await getPostById(listId);

    if (existingList.post_type !== PostType.List) {
      throw new Error(`Post ${listId} is not a List`);
    }

    const cityHint =
      (existingList.city_identifying_names?.[0] ?? '').trim()
      || existingList.primary_destination?.identifying_name?.trim()
      || '';

    const existingPostLocations: ListPostLocationPayload[] = (existingList.postLocations || []).map(
      (pl: PostLocation, i: number) => convertPostLocationToListFormat(pl, i, listId),
    );
    const existingCount = existingPostLocations.length;

    const ATOMIC_PLACE_ENRICH_CONCURRENCY = 4;
    const newSlots = await runWithConcurrencyLimit(
      places.map(
        (place, i) => async () => {
          const postLocationPayload = await convertPlaceToPostLocation(
            place,
            0,
            0,
            cityHint || undefined,
          );
          return listPostLocationPayloadFromEnrichedPlace(postLocationPayload, existingCount + i);
        },
      ),
      ATOMIC_PLACE_ENRICH_CONCURRENCY,
    );

    const existingCoverImageUrls = Array.isArray(existingList.image_urls)
      ? existingList.image_urls
      : [];

    // A list without a post-level cover keeps deriving one from its first slot.
    const { image_urls: _coverImageUrls, ...baseFields } = buildBasePostFields(
      existingList,
      existingCoverImageUrls,
    );

    const sourceMetadata = resolveSourceMetadataForAppend(
      existingList.source_metadata,
      appendOptions?.importSourceMetadata,
    );
    const existingListSocialId = mongoObjectIdStringOrNull(
      (existingList as { source_social_media_post_id?: string }).source_social_media_post_id,
    );
    const appendListSocialId = mongoObjectIdStringOrNull(appendOptions?.sourceSocialMediaPostId);
    const mergedListNotes = mergeOverviewNotesForAppend(
      existingList.list_notes,
      appendOptions?.appendOverviewNotes,
    );
    const destinationPatch = applyImportPrimaryDestinationForAppend(existingList, appendOptions);

    const updatePayload: ListPostPayload = {
      ...baseFields,
      _id: listId,
      post_type: 'List',
      name: existingList.name || 'Untitled List',
      body: existingList.body || existingList.name || 'Untitled List',
      category_ids: existingList.category_ids || [],
      ...(mergedListNotes ? { list_notes: mergedListNotes } : {}),
      ...(sourceMetadata !== undefined ? { source_metadata: sourceMetadata } : {}),
      ...(!existingListSocialId && appendListSocialId
        ? { source_social_media_post_id: appendListSocialId }
        : {}),
      ...destinationPatch,
      // Echo the existing cover photo back so adding places never replaces it.
      ...(existingCoverImageUrls.length > 0 ? { image_urls: existingCoverImageUrls } : {}),
      postLocations: [...existingPostLocations, ...newSlots],
    };

    return await createOrUpdateListPost(updatePayload as unknown as Record<string, unknown>);
  } catch (err) {
    error('[addPlacesToListAtomic] Error adding places to list:', err);
    throw err;
  }
}

/**
 * Add a place to the bottom of an existing list.
 *
 * @param listId - ID of the list to add place to
 * @param place  - Place card data to append
 * @returns Updated Post object
 */
export async function addPlaceToList(
  listId: string,
  place: PlaceCardData
): Promise<Post> {
  return addPlacesToListAtomic(listId, [place]);
}

/**
 * Create a new list with a place as the first slot.
 *
 * @param place - Place card data to add as first item
 * @param name  - Optional name for the new list (defaults to 'New List')
 * @param options - Optional destination metadata for new post creation
 * @returns Created Post object
 */
export async function createListWithPlace(
  place: PlaceCardData,
  name?: string,
  options: CreateWithPlaceOptions = {},
): Promise<Post> {
  try {
    const title = name?.trim() || 'New List';
    const cityHint =
      options.cityIdentifyingNames?.find((n) => typeof n === 'string' && n.trim())?.trim()
      || options.primaryDestination?.identifying_name?.trim()
      || '';
    const postLocationPayload = await convertPlaceToPostLocation(
      place,
      0,
      0,
      cityHint || undefined,
    );
    const newSlot = listPostLocationPayloadFromEnrichedPlace(postLocationPayload, 0);
    const coverImageUrls = deriveCoverImageUrlsFromPlace(place);

    const listNotes =
      typeof options.listNotes === 'string' ? options.listNotes.trim() : '';
    const sourceSocialMediaPostId = mongoObjectIdStringOrNull(options.sourceSocialMediaPostId);
    const createPayload: ListPostPayload = {
      post_type: 'List',
      name: title,
      body: title,
      is_draft: false,
      is_private: true,
      ...(listNotes ? { list_notes: listNotes } : {}),
      ...(coverImageUrls.length > 0 ? { image_urls: coverImageUrls } : {}),
      ...(options.cityIdentifyingNames ? { city_identifying_names: options.cityIdentifyingNames } : {}),
      ...(options.primaryDestination ? { primary_destination: options.primaryDestination } : {}),
      ...(options.sourceMetadata ? { source_metadata: options.sourceMetadata } : {}),
      ...(sourceSocialMediaPostId ? { source_social_media_post_id: sourceSocialMediaPostId } : {}),
      postLocations: [newSlot],
    };

    debug('[createListWithPlace] Creating list with place', {
      title,
      placeTitle: place.title,
      placeImageUrlsCount: place.imageUrls?.length ?? 0,
      coverImageUrlsCount: coverImageUrls.length,
      coverImageUrl: coverImageUrls[0]?.substring(0, 100),
      postLocationImageUrlsCount: newSlot.image_urls?.length ?? 0,
    });

    const createdPost = await createOrUpdateListPost(createPayload as unknown as Record<string, unknown>);

    debug('[createListWithPlace] List created', {
      postId: createdPost._id,
      postImageUrlsCount: createdPost.image_urls?.length ?? 0,
      postImageUrl: createdPost.image_urls?.[0]?.substring(0, 100),
      postLocationsCount: createdPost.postLocations?.length ?? 0,
    });

    return createdPost;
  } catch (err) {
    error('[createListWithPlace] Error creating list with place:', err);
    throw err;
  }
}

/**
 * Get the current user's lists (mirrors getUserItineraries).
 *
 * @param options - Query options
 * @returns Object with posts array and optional pagination info
 */
export async function getUserLists(
  options: GetUserItinerariesOptions = {}
): Promise<{ posts: SavedItinerary[]; pagination?: PaginationInfo }> {
  try {
    const { posts, pagination } = await getUserListPosts(options);
    return {
      posts: posts.map((post) => mapPostToSavedItinerary(post, 'Untitled List')),
      pagination,
    };
  } catch (err) {
    error('[getUserLists] Error fetching user lists:', err);
    throw err;
  }
}
